import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';
import { adminAPI, mediatorsAPI } from '../services/api';
import { ArrowLeft, Mail, Phone, Network as NetworkIcon, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const UNASSIGNED = '__none__';

export const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [mediators, setMediators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    load();
  }, [user, navigate]);

  const load = async () => {
    try {
      setLoading(true);
      const [u, m] = await Promise.all([
        adminAPI.getUsers(),
        mediatorsAPI.adminList()
      ]);
      if (u.success) setUsers(u.users);
      if (m.success) setMediators(m.mediators || []);
    } catch (error) {
      toast.error(error.message || 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleAssign = async (userId, newMediatorId) => {
    try {
      setSavingId(userId);
      const payload = newMediatorId === UNASSIGNED ? null : newMediatorId;
      await adminAPI.assignMediator(userId, payload);
      toast.success('Vermittler-Zuordnung aktualisiert');
      // Update local row optimistically
      const med = mediators.find((m) => m.id === payload);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? {
                ...u,
                referredByMediatorId: payload,
                referredByMediator: med
                  ? { id: med.id, firstName: med.firstName, lastName: med.lastName, referralCode: med.referralCode }
                  : null
              }
            : u
        )
      );
    } catch (err) {
      toast.error(err.message || 'Zuordnung fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (u) => {
    const msg = `Benutzer "${u.firstName} ${u.lastName}" (${u.email}) UNWIDERRUFLICH löschen?\n\n` +
      `Alle zugehörigen Zeichnungen werden ebenfalls gelöscht.\n` +
      `Bei Vermittlern werden zugeordnete Investoren wieder freigegeben.`;
    if (!window.confirm(msg)) return;
    try {
      setSavingId(u.id);
      const res = await adminAPI.deleteUser(u.id);
      toast.success(
        res?.deletedSubscriptions
          ? `Benutzer gelöscht (${res.deletedSubscriptions} Zeichnung(en) entfernt)`
          : 'Benutzer gelöscht'
      );
      setUsers((prev) => prev.filter((x) => x.id !== u.id));
    } catch (err) {
      toast.error(err.message || 'Löschen fehlgeschlagen');
    } finally {
      setSavingId(null);
    }
  };

  // Investors only for the assignment column
  const investors = users.filter((u) => u.role === 'investor');
  const others = users.filter((u) => u.role !== 'investor');

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-[#1a1a1a] to-black">
      <div className="container mx-auto px-4 py-8">
        <Button
          onClick={() => navigate('/admin/dashboard')}
          variant="ghost"
          className="text-[#C4A55A] hover:text-[#D4B56A] mb-4"
          data-testid="admin-users-back-btn"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Button>

        <h1 className="text-4xl font-bold text-white mb-2">Investoren-Übersicht</h1>
        <p className="text-gray-400 mb-8">
          Vermittler-Zuordnung kann hier nachträglich gesetzt oder geändert werden
        </p>

        {loading ? (
          <div className="text-center text-gray-400" data-testid="admin-users-loading">Lädt Benutzer...</div>
        ) : (
          <>
            <Card className="bg-[#1a1a1a] border-[#C4A55A]/20 mb-6">
              <CardHeader>
                <CardTitle className="text-white">
                  Registrierte Investoren ({investors.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                {investors.length === 0 ? (
                  <p className="text-gray-500 py-8 text-center" data-testid="admin-users-empty">
                    Noch keine Investoren registriert.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4A55A]/20">
                        <TableHead className="text-gray-400">Name</TableHead>
                        <TableHead className="text-gray-400">E-Mail</TableHead>
                        <TableHead className="text-gray-400">Telefon</TableHead>
                        <TableHead className="text-gray-400">Investment</TableHead>
                        <TableHead className="text-gray-400">Vermittler</TableHead>
                        <TableHead className="text-gray-400">Registriert</TableHead>
                        <TableHead className="text-gray-400 text-right">Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {investors.map((u) => (
                        <TableRow
                          key={u.id}
                          className="border-[#C4A55A]/20"
                          data-testid={`admin-users-row-${u.id}`}
                        >
                          <TableCell className="text-white">
                            {u.firstName} {u.lastName}
                          </TableCell>
                          <TableCell className="text-gray-300">
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-[#C4A55A]" />
                              {u.email}
                            </div>
                          </TableCell>
                          <TableCell className="text-gray-300">
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-[#C4A55A]" />
                              {u.phone || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="text-white font-semibold whitespace-nowrap">
                            CHF {u.investment?.toLocaleString('de-CH')}
                          </TableCell>
                          <TableCell className="min-w-[220px]">
                            <Select
                              value={u.referredByMediatorId || UNASSIGNED}
                              onValueChange={(v) => handleAssign(u.id, v)}
                              disabled={savingId === u.id || mediators.length === 0}
                            >
                              <SelectTrigger
                                className="bg-[#0a0a0a] border-[#C4A55A]/30 text-white h-9"
                                data-testid={`admin-users-mediator-select-${u.id}`}
                              >
                                <SelectValue placeholder="Nicht zugeordnet">
                                  {u.referredByMediator ? (
                                    <span className="flex items-center gap-2 text-sm">
                                      <NetworkIcon className="h-3.5 w-3.5 text-[#C4A55A]" />
                                      {u.referredByMediator.firstName} {u.referredByMediator.lastName}
                                      <span className="text-[#C4A55A] font-mono text-xs">
                                        {u.referredByMediator.referralCode}
                                      </span>
                                    </span>
                                  ) : (
                                    <span className="text-gray-500">— nicht zugeordnet —</span>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent className="bg-[#1a1a1a] border-[#C4A55A]/30 text-white">
                                <SelectItem value={UNASSIGNED}>
                                  — nicht zugeordnet —
                                </SelectItem>
                                {mediators.map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.firstName} {m.lastName}{' '}
                                    <span className="text-[#C4A55A] font-mono text-xs ml-1">
                                      {m.referralCode}
                                    </span>
                                    <span className="text-gray-500 text-xs ml-1">
                                      · {m.commissionRate}%
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-gray-400">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString('de-CH') : ''}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              size="sm"
                              variant="ghost"
                              disabled={savingId === u.id}
                              onClick={() => handleDelete(u)}
                              data-testid={`admin-users-delete-${u.id}`}
                              className="text-red-300 hover:bg-red-500/20 hover:text-white h-8"
                              title="Investor löschen"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>

            {others.length > 0 && (
              <Card className="bg-[#1a1a1a] border-[#C4A55A]/20">
                <CardHeader>
                  <CardTitle className="text-white">
                    Sonstige Konten ({others.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-[#C4A55A]/20">
                        <TableHead className="text-gray-400">Name</TableHead>
                        <TableHead className="text-gray-400">E-Mail</TableHead>
                        <TableHead className="text-gray-400">Rolle</TableHead>
                        <TableHead className="text-gray-400">Registriert</TableHead>
                        <TableHead className="text-gray-400 text-right">Aktion</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {others.map((u) => {
                        const isProtected = u.role === 'admin' || u.role === 'editor';
                        return (
                          <TableRow key={u.id} className="border-[#C4A55A]/20">
                            <TableCell className="text-white">{u.firstName} {u.lastName}</TableCell>
                            <TableCell className="text-gray-300">{u.email}</TableCell>
                            <TableCell>
                              <Badge className="bg-[#C4A55A]/20 text-[#C4A55A] border-[#C4A55A]/30">
                                {u.role}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-gray-400">
                              {u.createdAt ? new Date(u.createdAt).toLocaleDateString('de-CH') : ''}
                            </TableCell>
                            <TableCell className="text-right">
                              {isProtected ? (
                                <span className="text-xs text-gray-500">— geschützt —</span>
                              ) : (
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={savingId === u.id}
                                  onClick={() => handleDelete(u)}
                                  data-testid={`admin-users-delete-${u.id}`}
                                  className="text-red-300 hover:bg-red-500/20 hover:text-white h-8"
                                  title="Konto löschen"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </div>
  );
};
